import type { TFunction } from 'i18next';
import { dateTimeFormat } from '../i18n/format';

/**
 * Отображение меток времени.
 *
 * В БД всё в UTC (`timestamptz`), в местное время переводит только браузер —
 * поэтому форматирование живёт здесь, а не на сервере.
 *
 * Локаль берётся из выбранного языка, а не из настроек браузера (§11, фаза 10,
 * задача 7): человек, переключивший интерфейс на английский, ждёт и дат
 * по-английски, а до фазы 10 здесь стояло `ru-RU` строкой в коде.
 */

/**
 * Момент времени: секунды всегда, дата — когда она не сегодняшняя, год —
 * когда он не текущий.
 *
 * Голое время без даты обманчиво: «в этом состоянии с 20:44:23» одинаково
 * выглядит и для вчерашнего вечера, и для прошлой недели, а разница между
 * ними — это разница между «моргнуло ночью» и «лежит неделю». Показывать же
 * дату у события десятиминутной давности незачем: она занимает место
 * и ничего не добавляет.
 */
export function formatMoment(iso: string, language: string): string {
  const at = new Date(iso);
  const now = new Date();

  const time = dateTimeFormat(language, {
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(at);

  if (at.toDateString() === now.toDateString()) {
    return time;
  }

  const date = dateTimeFormat(language, {
    day: '2-digit',
    month: '2-digit',
    ...(at.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
  }).format(at);

  return `${date} ${time}`;
}

/**
 * Время правки файла: дата и время без секунд.
 *
 * Отдельно от `formatMoment` по двум причинам. Секунды у файла на диске
 * не значат ничего — их не с чем сравнивать, — а места занимают столько,
 * что строка `07.06.2011 09:28:32` перестаёт помещаться в колонку списка
 * и переносится на вторую строку, ломая высоту строк.
 *
 * Дата показывается всегда, включая сегодняшнюю: в списке файлов рядом
 * лежат вещи 2011 года и сегодняшние, и голое время среди дат читается
 * как пропуск. Год — только когда он не текущий, как и везде в платформе.
 */
export function formatFileTime(iso: string, language: string): string {
  const at = new Date(iso);

  if (Number.isNaN(at.getTime())) {
    return '—';
  }

  const date = dateTimeFormat(language, {
    day: '2-digit',
    month: '2-digit',
    ...(at.getFullYear() === new Date().getFullYear() ? {} : { year: 'numeric' }),
  }).format(at);

  const time = dateTimeFormat(language, {
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
  }).format(at);

  return `${date} ${time}`;
}

/**
 * Возраст словами. Секунды до минуты, дальше крупнее: точность здесь не нужна,
 * нужен порядок величины и то, что число растёт.
 *
 * Формы множественного числа берёт словарь: «1 с назад» и «5 с назад»
 * в русском совпадают, а «1 minute ago» и «2 minutes ago» — нет, и подставить
 * число в готовую фразу здесь не выйдет (§11, фаза 10, задача 7).
 */
export function formatAgo(ageSec: number, t: TFunction): string {
  if (ageSec < 5) {
    return t('common.ago.justNow');
  }
  if (ageSec < 60) {
    return t('common.ago.seconds', { count: ageSec });
  }
  if (ageSec < 3600) {
    return t('common.ago.minutes', { count: Math.floor(ageSec / 60) });
  }
  if (ageSec < 86_400) {
    return t('common.ago.hours', { count: Math.floor(ageSec / 3600) });
  }
  return t('common.ago.days', { count: Math.floor(ageSec / 86_400) });
}
